import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { MdClose } from 'react-icons/md';
import {
  LuPenLine,
  LuDownload,
  LuCalendarDays,
  LuSparkles,
} from 'react-icons/lu';
import {
  FaFacebook,
  FaInstagram,
  FaLinkedinIn,
  FaTiktok,
} from 'react-icons/fa';
import { XTwitter } from '../../../../assets/svgs/icons';
import { normalizeNetworkList } from '../../../../shared/campaign/utils/socialNetworkMapper';
import { PublicationStatusBadge } from '../../../../shared/campaign/PublicationStatusBadge';
import MarkdownPreview from '../../../../shared/campaign/MarkdownPreview';
import { useAutoHideScrollbar } from '../../hooks/useAutoHideScrollbar';
import {
  resolveSessionUpdatedAtToken,
  withArtDisplayCacheBust,
} from '../../../../shared/campaign/utils/artDisplayCacheBust';
import styles from './styles.module.css';

export const ArtPreview = ({
  artResults = [],
  artStatus = 'idle',
  artProgress = { current: 0, total: 0, progress: 0 },
  editingPieceNumbers = [],
  strategyMarkdown = '',
  showArtsTab: showArtsTabProp,
  scheduleData: scheduleDataProp = {},
  onClose,
  isCalendarTabEnabled = true,
  isApproved = false,
  canUseAI = true,
  activeTab: activeTabProp = 'artes',
  selectedImage: selectedImageProp,
  onSelectImage,
  imageRenderKey: imageRenderKeyProp = 0,
  sessionDetail = null,
  scheduleSyncWarning = false,
  onScheduleDataChange,
  isScheduleReadOnly = false,
  onStrategySave,
  isStrategyEditDisabled = true,
}) => {
  const { t } = useTranslation();

  const [localSelected, setLocalSelected] = useState(null);
  const [subTab, setSubTab] = useState('ai');
  const [localScheduleData, setLocalScheduleData] = useState({
    caption: '',
    publishDate: '',
    publishTime: '',
    selectedNetworks: [],
    publicationStatus: '',
    ...scheduleDataProp,
  });

  const thumbnailCarouselRef = useRef(null);
  const { ref: thumbnailScrollRef, scrollClassName: thumbnailScrollClassName } =
    useAutoHideScrollbar(thumbnailCarouselRef, { orientation: 'horizontal' });

  const selectedImage = selectedImageProp || localSelected || artResults[0] || null;
  const imageRenderKey = imageRenderKeyProp;
  const sessionUpdatedAt = resolveSessionUpdatedAtToken(sessionDetail);
  const skeletonCount = Math.max(0, (artProgress.total || 0) - artResults.length);

  const isGenerating = artStatus === 'generating';
  const isEditing = artStatus === 'editing';

  const getPieceNumber = (art) => {
    if (!art || typeof art !== 'object') {
      return null;
    }
    const pieceNumber = art.pieceNumber ?? art.piece_number;
    return typeof pieceNumber === 'number' ? pieceNumber : null;
  };

  const isPieceEditing = (pieceNumber) => {
    if (pieceNumber == null) {
      return isEditing;
    }
    if (Array.isArray(editingPieceNumbers) && editingPieceNumbers.length > 0) {
      return editingPieceNumbers.includes(pieceNumber);
    }
    return isEditing;
  };

  const selectedPieceNumber = getPieceNumber(selectedImage);
  const isSelectedImageEditing = isPieceEditing(selectedPieceNumber);

  const showArts =
    showArtsTabProp !== undefined
      ? showArtsTabProp
      : isGenerating || isEditing || artResults.length > 0;
  const showStrategy = !!strategyMarkdown;

  const scheduleData = { ...localScheduleData, ...scheduleDataProp };

  useEffect(() => {
    if (artResults.length > 0 && !selectedImageProp) {
      setLocalSelected((prev) => {
        if (!prev) {
          return artResults[0];
        }
        const pieceNumber = prev.pieceNumber ?? prev.piece_number;
        const updated = artResults.find(
          (art) => (art.pieceNumber ?? art.piece_number) === pieceNumber,
        );
        return updated ?? artResults[0];
      });
    }
  }, [artResults, selectedImageProp]);

  useEffect(() => {
    if (isApproved && subTab === 'ai') {
      setSubTab(isCalendarTabEnabled ? 'edit' : 'download');
    }
  }, [isApproved, isCalendarTabEnabled, subTab]);

  useEffect(() => {
    if ((isApproved || !canUseAI) && subTab === 'ai') {
      setSubTab(isCalendarTabEnabled ? 'edit' : 'download');
    }
  }, [isApproved, canUseAI, subTab, isCalendarTabEnabled]);

  useEffect(() => {
    if (!isCalendarTabEnabled && subTab === 'edit') {
      setSubTab('ai');
    }
  }, [subTab, isCalendarTabEnabled]);

  const handleSelectImage = useCallback(
    (art) => {
      if (!selectedImageProp) {
        setLocalSelected(art);
      }
      if (onSelectImage) {
        onSelectImage(art);
      }
    },
    [selectedImageProp, onSelectImage],
  );

  const resolveSelectedSrc = useCallback(() => {
    if (!selectedImage) return null;
    if (typeof selectedImage === 'string') return selectedImage;
    return selectedImage.url || selectedImage.imageUrl || null;
  }, [selectedImage]);

  const cacheBustOptions = useCallback(
    (revision = null) => ({
      renderKey: imageRenderKey,
      revision,
      sessionUpdatedAt,
    }),
    [imageRenderKey, sessionUpdatedAt],
  );

  const resolveMainImgSrcForDisplay = useCallback(() => {
    const raw = resolveSelectedSrc();
    const revision =
      typeof selectedImage === 'object' && selectedImage
        ? selectedImage.revision
        : null;
    if (typeof raw === 'string' && raw) {
      return withArtDisplayCacheBust(raw, cacheBustOptions(revision));
    }
    if (typeof selectedImage === 'string') {
      return withArtDisplayCacheBust(
        selectedImage,
        cacheBustOptions(revision),
      );
    }
    return '';
  }, [resolveSelectedSrc, selectedImage, cacheBustOptions]);

  const thumbSrcForDisplay = useCallback(
    (image) => {
      const raw =
        typeof (image.url || image.imageUrl || image) === 'string'
          ? image.url || image.imageUrl || image
          : '';
      return withArtDisplayCacheBust(raw, cacheBustOptions(image?.revision));
    },
    [cacheBustOptions],
  );

  const handleDownloadSelected = useCallback(() => {
    const url = resolveSelectedSrc();
    if (!url) return;
    const link = document.createElement('a');
    link.href = url;
    link.download = `arte-${selectedImage?.pieceNumber || 'download'}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [resolveSelectedSrc, selectedImage]);

  const handleScheduleChange = useCallback(
    (field, value) => {
      if (isScheduleReadOnly) return;
      const updated = { ...scheduleData, [field]: value };
      setLocalScheduleData(updated);
      if (onScheduleDataChange) {
        onScheduleDataChange(
          field,
          value,
          selectedImage?.pieceNumber ?? selectedImage?.piece_number,
        );
      }
    },
    [isScheduleReadOnly, scheduleData, onScheduleDataChange],
  );

  const handleDateChange = useCallback(
    (e) => {
      let value = e.target.value.replace(/\D/g, '');
      if (value.length > 8) value = value.slice(0, 8);
      if (value.length > 2) {
        value = `${value.slice(0, 2)}/${value.slice(2)}`;
      }
      if (value.length > 5) {
        value = `${value.slice(0, 5)}/${value.slice(5)}`;
      }
      handleScheduleChange('publishDate', value);
    },
    [handleScheduleChange],
  );

  const handleTimeChange = useCallback(
    (e) => {
      let value = e.target.value.replace(/\D/g, '');
      if (value.length > 4) value = value.slice(0, 4);
      if (value.length > 2) {
        value = `${value.slice(0, 2)}:${value.slice(2)}`;
      }
      handleScheduleChange('publishTime', value);
    },
    [handleScheduleChange],
  );

  const socialNetworks = [
    { id: 'facebook', Icon: FaFacebook },
    { id: 'instagram', Icon: FaInstagram },
    { id: 'linkedin', Icon: FaLinkedinIn },
    { id: 'tiktok', Icon: FaTiktok },
    { id: 'twitter', Icon: null },
  ];

  const selectedNetworks = normalizeNetworkList(
    scheduleData.selectedNetworks || [],
  );
  const caption = scheduleData.caption || '';
  const publishDate = scheduleData.publishDate || '';
  const publishTime = scheduleData.publishTime || '';

  if (!showArts && !showStrategy) {
    return null;
  }

  return (
    <div className={`${styles.previewContainer} ${activeTabProp === 'estrategia' ? styles.previewContainerStrategy : ''}`}>
      {activeTabProp === 'artes' && (
        <>
          <div
            style={{
              maxWidth: '100%',
              width: '100%',
              flex: 1,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
              overflow: 'auto',
              justifyContent: 'flex-start',
            }}
          >
            {/* Thumbnail carousel — visible only in AI mode */}
            {subTab === 'ai' && (
              <div
                ref={thumbnailScrollRef}
                className={`${styles.thumbnailCarousel} ${thumbnailScrollClassName}`}
              >
                {artResults.map((art, index) => {
                  const pieceNumber = getPieceNumber(art);
                  const thumbIsEditing = isPieceEditing(pieceNumber);
                  return (
                  <div
                    key={`thumb-${art.pieceNumber ?? index}-${imageRenderKey}-${art.revision ?? ''}-${art.url || art.imageUrl || ''}`}
                    className={`${styles.thumbnailItem} ${art === selectedImage ? styles.thumbnailActive : ''} ${thumbIsEditing ? styles.thumbnailItemEditing : ''}`}
                    onClick={() => handleSelectImage(art)}
                  >
                    <img
                      src={thumbSrcForDisplay(art)}
                      alt={t('chatsocket.preview_art.art_fallback', {
                        index: art.pieceNumber || index + 1,
                      })}
                      className={thumbIsEditing ? styles.thumbnailImageEditing : ''}
                    />
                    {thumbIsEditing && (
                      <div className={styles.thumbnailEditingOverlay}>
                        <div className={styles.editingSpinner} />
                      </div>
                    )}
                  </div>
                );
                })}

                {Array.from({ length: skeletonCount }).map((_, index) => (
                  <div
                    key={`skeleton-${index}`}
                    className={styles.thumbnailSkeleton}
                  >
                    <div className={styles.thumbnailSkeletonInner} />
                  </div>
                ))}
              </div>
            )}

            {/* Main image + close + floating buttons */}
            <div className={styles.mainImageRow}>
              {onClose && (
                <div
                  style={{
                    display: 'flex',
                    height: '100%',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <button
                    className={styles.closeButton}
                    onClick={onClose}
                    title={t('chatsocket.preview_art.close')}
                  >
                    <MdClose size={18} />
                  </button>
                </div>
              )}

              <div className={styles.mainImageWrapper}>
                <img
                  src={resolveMainImgSrcForDisplay()}
                  alt={t('chatsocket.preview_art.preview_alt')}
                  className={`${styles.previewImage} ${isSelectedImageEditing ? styles.previewImageEditing : ''}`}
                />
                {scheduleData.publicationStatus && (
                  <div className={styles.statusOverlay}>
                    <PublicationStatusBadge
                      status={scheduleData.publicationStatus}
                    />
                  </div>
                )}
                {isSelectedImageEditing && (
                  <div className={styles.editingOverlay}>
                    <div className={styles.editingSpinner} />
                    <span className={styles.editingText}>
                      {t('chatsocket.preview_art.editing')}
                    </span>
                  </div>
                )}
              </div>

              <div
                style={{
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <div className={styles.floatingButtonsPill}>
                  <button
                    className={`${styles.floatingButtonSecondary} ${subTab === 'ai' ? styles.floatingButtonActive : ''} ${(isApproved || !canUseAI) ? styles.floatingButtonDisabled : ''}`}
                    title={
                      isApproved
                        ? t('chatsocket.preview_art.editing_disabled')
                        : !canUseAI
                          ? t('chatsocket.preview_art.ai_unavailable')
                          : t('chatsocket.preview_art.ai_default')
                    }
                    onClick={() => {
                      if (!isApproved && canUseAI) {
                        setSubTab('ai');
                      }
                    }}
                    disabled={isApproved || !canUseAI}
                  >
                    <LuPenLine size={20} />
                  </button>
                  <button
                    className={`${styles.floatingButtonSecondary} ${subTab === 'edit' && isCalendarTabEnabled ? styles.floatingButtonActive : ''} ${!isCalendarTabEnabled ? styles.floatingButtonDisabled : ''}`}
                    title={
                      isCalendarTabEnabled
                        ? t('chatsocket.preview_art.schedule_tooltip')
                        : t('chatsocket.preview_art.schedule_disabled')
                    }
                    onClick={() => {
                      if (isCalendarTabEnabled) {
                        setSubTab('edit');
                      }
                    }}
                    disabled={!isCalendarTabEnabled}
                  >
                    <LuCalendarDays size={24} />
                  </button>
                  <button
                    className={`${styles.floatingButtonSecondary} ${subTab === 'download' ? styles.floatingButtonActive : ''}`}
                    title={t('chatsocket.preview_art.download')}
                    onClick={() => {
                      setSubTab('download');
                      handleDownloadSelected();
                    }}
                  >
                    <LuDownload size={20} />
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Detail panel: caption, date, social networks */}
          {subTab === 'edit' && (
            <div className={styles.detailsContainer}>
              {scheduleSyncWarning && (
                <p className={styles.scheduleSyncWarning} role="status">
                  {t('chatsocket.preview_art.schedule_sync_warning')}
                </p>
              )}
              <div className={styles.detailsSection}>
                <span className={styles.detailsLabel}>
                  {t('chatsocket.preview_art.caption_label')}
                </span>
                <div className={styles.captionInputContainer}>
                  <textarea
                    className={styles.captionInput}
                    placeholder={t('chatsocket.preview_art.caption_placeholder')}
                    value={caption}
                    onChange={(e) =>
                      handleScheduleChange('caption', e.target.value)
                    }
                    rows={3}
                    disabled={isScheduleReadOnly}
                  />
                  <button
                    className={styles.captionSparkleButton}
                    title={t('chatsocket.preview_art.generate_ai')}
                    disabled={isScheduleReadOnly}
                  >
                    <LuSparkles size={20} />
                  </button>
                </div>
              </div>

              <div className={styles.detailsBottomRow}>
                <div className={styles.detailsSection}>
                  <span className={styles.detailsLabel}>
                    {t('chatsocket.preview_art.date_label')}
                  </span>
                  <div className={styles.dateTimeRow}>
                    <div className={styles.dateTimeField}>
                      <LuCalendarDays size={24} color="#9002CD" />
                      <input
                        type="text"
                        className={styles.dateTimeInput}
                        placeholder={t('chatsocket.preview_art.date_placeholder')}
                        value={publishDate}
                        onChange={handleDateChange}
                        maxLength={10}
                        disabled={isScheduleReadOnly}
                      />
                    </div>
                    <div className={styles.dateTimeField}>
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 20 20"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M15 1.33989C16.5083 2.21075 17.7629 3.46042 18.6398 4.96519C19.5167 6.46997 19.9854 8.17766 19.9994 9.91923C20.0135 11.6608 19.5725 13.3758 18.72 14.8946C17.8676 16.4133 16.6332 17.6831 15.1392 18.5782C13.6452 19.4733 11.9434 19.9627 10.2021 19.998C8.46083 20.0332 6.74055 19.6131 5.21155 18.7791C3.68256 17.9452 2.39787 16.7264 1.48467 15.2434C0.571462 13.7604 0.0614093 12.0646 0.00500011 10.3239L0 9.99989L0.00500011 9.67589C0.0610032 7.94888 0.563548 6.26585 1.46364 4.79089C2.36373 3.31592 3.63065 2.09934 5.14089 1.25977C6.65113 0.420205 8.35315 -0.0137108 10.081 0.000330246C11.8089 0.0143713 13.5036 0.47589 15 1.33989ZM10 3.99989C9.75507 3.99992 9.51866 4.08985 9.33563 4.25261C9.15259 4.41537 9.03566 4.63964 9.007 4.88289L9 4.99989V9.99989L9.009 10.1309C9.0318 10.3044 9.09973 10.4689 9.206 10.6079L9.293 10.7079L12.293 13.7079L12.387 13.7899C12.5624 13.926 12.778 13.9998 13 13.9998C13.222 13.9998 13.4376 13.926 13.613 13.7899L13.707 13.7069L13.79 13.6129C13.9261 13.4375 13.9999 13.2219 13.9999 12.9999C13.9999 12.7779 13.9261 12.5623 13.79 12.3869L13.707 12.2929L11 9.58489V4.99989L10.993 4.88289C10.9643 4.63964 10.8474 4.41537 10.6644 4.25261C10.4813 4.08985 10.2449 3.99992 10 3.99989Z"
                          fill="#9002CD"
                        />
                      </svg>
                      <input
                        type="text"
                        className={styles.dateTimeInput}
                        placeholder={t('chatsocket.preview_art.time_placeholder')}
                        value={publishTime}
                        onChange={handleTimeChange}
                        maxLength={5}
                        disabled={isScheduleReadOnly}
                      />
                    </div>
                  </div>
                </div>

                <div className={styles.detailsSection}>
                  <span className={styles.detailsLabel}>
                    {t('chatsocket.preview_art.networks_label')}
                  </span>
                  <div className={styles.socialRow}>
                    {socialNetworks
                      .filter(({ id }) => selectedNetworks.includes(id))
                      .map(({ id, Icon }) => (
                        <button
                          key={id}
                          className={`${styles.socialButton} ${styles.socialButtonActive}`}
                          title={id}
                          disabled={isScheduleReadOnly}
                        >
                          {Icon ? (
                            <Icon size={24} />
                          ) : (
                            <XTwitter
                              width={24}
                              height={24}
                              color="currentColor"
                            />
                          )}
                        </button>
                      ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {activeTabProp === 'estrategia' && strategyMarkdown && (
        <MarkdownPreview
          key={strategyMarkdown.slice(0, 100)}
          content={strategyMarkdown}
          isEditDisabled={isStrategyEditDisabled}
          onSave={onStrategySave}
          defaultEditable
        />
      )}
    </div>
  );
};
